import axios from 'axios'

// ============================================================================
// API CONFIGURATION
// ============================================================================
const atarrayaApiProducer = axios.create({
  baseURL: process.env.VUE_APP_ATARRAYA_API_URL + '/v3/identity',
  timeout: process.env.VUE_APP_ATARRAYA_TIMEOUT
})

// ============================================================================
// CONSTANTS
// ============================================================================
const API_ENDPOINTS = {
  PRODUCERS: '/tenants'
}

const API_HEADERS = {
  'x-api-key': process.env.VUE_APP_ATARRAYA_PRODUCER_API_KEY
}

// ============================================================================
// STATE
// ============================================================================
export const state = {
  producers: [],
  simpleProducers: []
}

// ============================================================================
// GETTERS
// ============================================================================
export const getters = {
  producers: state => state.producers,
  simpleProducers: state => state.simpleProducers,
  producerById: state => id => {
    return state.producers.find(producer => producer.id === id)
  },
  hasProducers: state => state.producers.length > 0,
  producersCount: state => state.producers.length
}

// ============================================================================
// MUTATIONS
// ============================================================================
export const mutations = {
  LOAD_PRODUCERS(state, producers) {
    state.producers = producers
  },

  LOAD_SIMPLE_PRODUCERS(state, producers) {
    state.simpleProducers = producers
  },

  ADD_PRODUCER(state, producer) {
    state.producers.push(producer)
  },

  UPDATE_PRODUCER(state, updatedProducer) {
    const index = state.producers.findIndex(p => p.id === updatedProducer.id)
    if (index !== -1) {
      state.producers.splice(index, 1, updatedProducer)
    }
  },

  REMOVE_PRODUCER(state, producerId) {
    state.producers = state.producers.filter(p => p.id !== producerId)
  },

  CLEAR_PRODUCERS(state) {
    state.producers = []
    state.simpleProducers = []
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const makeApiRequest = async (method, endpoint, data = null) => {
  const config = {
    headers: API_HEADERS
  }

  try {
    let response
    switch (method) {
      case 'GET':
        response = await atarrayaApiProducer.get(endpoint, config)
        break
      case 'POST':
        response = await atarrayaApiProducer.post(endpoint, data, config)
        break
      case 'PUT':
        response = await atarrayaApiProducer.put(endpoint, data, config)
        break
      case 'DELETE':
        response = await atarrayaApiProducer.delete(endpoint, config)
        break
      default:
        throw new Error(`Unsupported method: ${method}`)
    }
    return response.data
  } catch (error) {
    console.error(`Producer API ${method} error:`, error)
    throw error
  }
}

// ============================================================================
// ACTIONS
// ============================================================================
export const actions = {
  async getProducers({ commit }) {
    try {
      const response = await makeApiRequest('GET', API_ENDPOINTS.PRODUCERS)
      const producers = response.data || []
      commit('LOAD_PRODUCERS', producers)
      return producers
    } catch (error) {
      console.error('Error loading producers:', error)
      throw error
    }
  },

  async getSimpleProducers({ commit }) {
    try {
      const response = await makeApiRequest('GET', API_ENDPOINTS.PRODUCERS)
      const producers = response.data || []
      commit('LOAD_SIMPLE_PRODUCERS', producers)
      return producers
    } catch (error) {
      console.error('Error loading simple producers:', error)
      throw error
    }
  },

  async addProducer({ commit }, producerData) {
    try {
      const response = await makeApiRequest('POST', API_ENDPOINTS.PRODUCERS, producerData)
      const newProducer = response.data
      commit('ADD_PRODUCER', newProducer)
      return newProducer
    } catch (error) {
      console.error('Error adding producer:', error)
      throw error
    }
  },

  async updateProducer({ commit }, { id, data }) {
    try {
      const endpoint = `${API_ENDPOINTS.PRODUCERS}/${id}`
      const response = await makeApiRequest('PUT', endpoint, data)
      const updatedProducer = response.data
      commit('UPDATE_PRODUCER', updatedProducer)
      return updatedProducer
    } catch (error) {
      console.error('Error updating producer:', error)
      throw error
    }
  },

  async deleteProducer({ commit }, producerId) {
    try {
      const endpoint = `${API_ENDPOINTS.PRODUCERS}/${producerId}`
      await makeApiRequest('DELETE', endpoint)
      commit('REMOVE_PRODUCER', producerId)
      return true
    } catch (error) {
      console.error('Error deleting producer:', error)
      throw error
    }
  },

  clearProducersData({ commit }) {
    commit('CLEAR_PRODUCERS')
  }
}